import {
  MigrationRunDetails,
  MigrationRunDetailsStatus,
  useGetMigrations,
  useInvalidateUseGetMigrations,
  useStartMigrationRun,
} from '@urbanx/agx.support.client';
import {
  AgxBadge,
  AgxButton,
  AgxColumn,
  AgxModal,
  AgxRow,
  AgxTable,
  AgxTextArea,
  AgxTypography,
  IColumnConfig,
  Icon_Refresh,
  useToast,
} from '@urbanx/agx.ui-components2';
import Panel from 'components/panel/Panel';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import SupportBackButton from '../SupportBackButton';
import { groupMigrations } from './groupMigrations';
import { createMigrationManifest, getIntroducedMigrations } from './migrationManifest';
import './Migrations.scss';

const tableColumns: (string | IColumnConfig)[] = [
  'Migration',
  'Status',
  'Error',
  { name: 'Action', align: 'right' },
];

const statusConfig = {
  [MigrationRunDetailsStatus.NotRun]: {
    label: 'Not run',
    colour: 'lightgrey',
  },
  [MigrationRunDetailsStatus.InProgress]: {
    label: 'In progress',
    colour: 'blue',
  },
  [MigrationRunDetailsStatus.Successful]: {
    label: 'Successful',
    colour: 'green',
  },
  [MigrationRunDetailsStatus.Failed]: {
    label: 'Failed',
    colour: 'red',
  },
} as const;

const formatDateTime = (date: string) => dayjs(date).format('DD/MM/YYYY h:mm A');

const getMigrationStatus = (migration: MigrationRunDetails) =>
  migration.status ?? MigrationRunDetailsStatus.NotRun;

const getMigrationError = (migration: MigrationRunDetails) =>
  migration.errorMessage || migration.additionalInformation || migration.resultLog;

const Migrations = () => {
  const { pushToast } = useToast();
  const [pendingMigrationTitle, setPendingMigrationTitle] = useState<string>();
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);
  const [pastedMigrationManifest, setPastedMigrationManifest] = useState('');
  const [migrationError, setMigrationError] = useState<string>();

  const {
    data: migrations,
    isPending: isLoadingMigrations,
    isError: hasMigrationsError,
  } = useGetMigrations();

  const invalidateMigrations = useInvalidateUseGetMigrations();

  const { startMigrationRun, startMigrationRunPending } = useStartMigrationRun({
    onSuccess: async () => {
      setPendingMigrationTitle(undefined);
      await invalidateMigrations();
      pushToast({
        message: 'Migration run queued',
        type: 'success',
      });
    },
    onError: () => {
      setPendingMigrationTitle(undefined);
      pushToast({
        message: 'Error queueing migration run',
        type: 'error',
      });
    },
  });

  const hasInProgressMigration = useMemo(
    () =>
      (migrations ?? []).some(
        (migration) => getMigrationStatus(migration) === MigrationRunDetailsStatus.InProgress
      ),
    [migrations]
  );
  const migrationTitles = useMemo(
    () => (migrations ?? []).map((migration) => migration.title),
    [migrations]
  );
  const introducedMigrations = useMemo(
    () => getIntroducedMigrations(pastedMigrationManifest, migrationTitles),
    [migrationTitles, pastedMigrationManifest]
  );
  const hasPastedMigrationManifest = pastedMigrationManifest
    .split(/\r?\n/)
    .some((title) => title.trim());
  const manifestActionsDisabled = isLoadingMigrations || hasMigrationsError;

  useEffect(() => {
    if (!hasInProgressMigration) return;

    const intervalId = window.setInterval(() => {
      void invalidateMigrations();
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [hasInProgressMigration, invalidateMigrations]);

  const queueMigrationRun = useCallback(
    async (migrationTitle?: string) => {
      setPendingMigrationTitle(migrationTitle ?? 'all');
      await startMigrationRun(
        migrationTitle
          ? {
              migrationTitle,
            }
          : {}
      );
    },
    [startMigrationRun]
  );

  const copyMigrationList = async () => {
    try {
      await navigator.clipboard.writeText(createMigrationManifest(migrationTitles));
      pushToast({
        message: 'Migration list copied',
        type: 'success',
      });
    } catch {
      pushToast({
        message: 'Unable to copy migration list',
        type: 'error',
      });
    }
  };

  const setCompareModalOpen = (isOpen: boolean) => {
    setIsCompareModalOpen(isOpen);
    if (!isOpen) setPastedMigrationManifest('');
  };

  const rows = new Map(
    (migrations ?? []).map((migration, index) => {
      const status = getMigrationStatus(migration);
      const config = statusConfig[status];
      const errorMessage = getMigrationError(migration);
      const migrationTitle = migration.title ?? '';
      const isPending = startMigrationRunPending && pendingMigrationTitle === migrationTitle;
      const recordCounts =
        typeof migration.successfulRecords === 'number' &&
        typeof migration.failedRecords === 'number'
          ? {
              successful: migration.successfulRecords,
              failed: migration.failedRecords,
              total: migration.successfulRecords + migration.failedRecords,
            }
          : undefined;

      return [
        migrationTitle,
        {
          Migration: (
            <AgxColumn gap="spacing-xs">
              <AgxTypography
                id={`support-migration-${index}-display-name`}
                type="body_ui_strong"
                size="small"
              >
                {migration.displayName ?? migrationTitle}
              </AgxTypography>
              <AgxTypography id={`support-migration-${index}-title`} type="body_ui" size="small">
                {migrationTitle}
              </AgxTypography>
            </AgxColumn>
          ),
          Status: (
            <AgxColumn gap="spacing-xs">
              <AgxBadge type="colourfill" size="sm" text={config.label} colour={config.colour} />
              {recordCounts && (
                <AgxTypography
                  id={`support-migration-${index}-record-counts`}
                  type="caption"
                  size="small"
                  color="var(--text-secondary)"
                >
                  {recordCounts.successful} succeeded · {recordCounts.failed} failed ·{' '}
                  {recordCounts.total === 0
                    ? 'N/A'
                    : `${Math.round((recordCounts.successful / recordCounts.total) * 100)}%`}{' '}
                  success
                </AgxTypography>
              )}
            </AgxColumn>
          ),
          Error:
            status === MigrationRunDetailsStatus.Failed && errorMessage ? (
              <AgxButton
                id={`support-migration-${index}-error`}
                text="View error"
                variant="hollow"
                size="small"
                onClick={() => setMigrationError(errorMessage)}
              />
            ) : undefined,
          Action: (
            <AgxButton
              id={`support-migration-${index}-run`}
              text={isPending ? 'Queueing' : 'Run'}
              variant="hollow"
              size="small"
              leftIcon={<Icon_Refresh width={16} height={16} />}
              disabled={!migration.canRun || startMigrationRunPending}
              onClick={() => void queueMigrationRun(migrationTitle)}
            />
          ),
        },
      ] as const;
    })
  );

  const groupedMigrations = groupMigrations(migrations ?? []);
  const sections = [
    ...(groupedMigrations.notRun.length
      ? [{ id: 'not-run', title: 'Not run', migrations: groupedMigrations.notRun }]
      : []),
    ...groupedMigrations.runs.map((run) => ({
      id: run.id,
      title: `${formatDateTime(run.startedAt)} - ${run.id}`,
      migrations: run.migrations,
    })),
    ...(groupedMigrations.previousRuns.length
      ? [
          {
            id: 'previous-runs',
            title: 'Previous runs',
            migrations: groupedMigrations.previousRuns,
          },
        ]
      : []),
  ];

  return (
    <AgxColumn gap="spacing-xl" fill>
      <SupportBackButton />
      <Panel
        title="Migrations"
        actionContent={
          <AgxRow gap="sm" alignItems="alignCenter">
            <AgxButton
              id="support-migrations-copy-list"
              text="Copy migration list"
              variant="hollow"
              size="small"
              disabled={manifestActionsDisabled}
              onClick={() => void copyMigrationList()}
            />
            <AgxButton
              id="support-migrations-compare-list"
              text="Compare migration list"
              variant="hollow"
              size="small"
              disabled={manifestActionsDisabled}
              onClick={() => setCompareModalOpen(true)}
            />
            <AgxButton
              id="support-migrations-run-all"
              text={pendingMigrationTitle === 'all' ? 'Queueing' : 'Run all'}
              variant="primary"
              size="small"
              leftIcon={<Icon_Refresh width={16} height={16} />}
              disabled={startMigrationRunPending}
              onClick={() => void queueMigrationRun()}
            />
          </AgxRow>
        }
      >
        <AgxColumn gap="xl" fill>
          {hasMigrationsError && (
            <AgxTypography id="support-migrations-error" type="body" size="small">
              Unable to load migrations.
            </AgxTypography>
          )}

          {isLoadingMigrations || sections.length === 0 ? (
            <AgxTable
              columns={tableColumns}
              rows={[]}
              tableStyle="square"
              showLoadingState={isLoadingMigrations}
              noDataLabel="No migrations found"
            />
          ) : (
            sections.map((section) => (
              <section key={section.id} className="migrationsSection">
                <div className="migrationsSection__heading">
                  <AgxTypography
                    id={`support-migrations-${section.id}`}
                    type="heading"
                    size="small"
                  >
                    {section.title}
                  </AgxTypography>
                </div>
                <AgxTable
                  columns={tableColumns}
                  rows={section.migrations.map((migration) => rows.get(migration.title ?? '')!)}
                  tableStyle="square"
                />
              </section>
            ))
          )}
        </AgxColumn>
      </Panel>
      <AgxModal
        id="support-migrations-compare-modal"
        title="Compare migration list"
        type="single"
        variant="withDivider"
        actionButtonContainerStyle="withoutDivider"
        isOpen={isCompareModalOpen}
        setIsDialogOpen={setCompareModalOpen}
        content={
          <AgxColumn gap="spacing-lg" padding="spacing-4xl" fill>
            <AgxTypography id="support-migrations-compare-description" type="body" size="small">
              Paste the migration list from the build you intend to deploy to this environment.
            </AgxTypography>
            <AgxTextArea
              id="support-migrations-compare-input"
              label="Migration list"
              placeholder="Paste migration names here"
              defaultValue={pastedMigrationManifest}
              onInputValueChange={({ value }) => setPastedMigrationManifest(value)}
              rows={8}
              hideOptionalText
              verticalResizeOnly
              stretch
            />
            {hasPastedMigrationManifest && (
              <AgxColumn gap="spacing-md" fill>
                <AgxTypography
                  id="support-migrations-compare-result-count"
                  type="heading"
                  size="small"
                >
                  {introducedMigrations.length}{' '}
                  {introducedMigrations.length === 1
                    ? 'introduced migration'
                    : 'introduced migrations'}
                </AgxTypography>
                {introducedMigrations.length === 0 ? (
                  <AgxTypography id="support-migrations-compare-empty" type="body_ui" size="small">
                    No introduced migrations
                  </AgxTypography>
                ) : (
                  introducedMigrations.map((title, index) => (
                    <AgxColumn key={title} gap="spacing-xs">
                      <AgxTypography
                        id={`support-migrations-compare-${index}-display-name`}
                        type="body_ui_strong"
                        size="small"
                      >
                        {title.split('.').at(-1)}
                      </AgxTypography>
                      <AgxTypography
                        id={`support-migrations-compare-${index}-title`}
                        type="body_ui"
                        size="small"
                      >
                        {title}
                      </AgxTypography>
                    </AgxColumn>
                  ))
                )}
              </AgxColumn>
            )}
          </AgxColumn>
        }
      />
      <AgxModal
        id="support-migrations-error-modal"
        title="Migration error"
        type="fullScreen"
        variant="withDivider"
        actionButtonContainerStyle="withoutDivider"
        isOpen={migrationError !== undefined}
        setIsDialogOpen={(isOpen) => {
          if (!isOpen) setMigrationError(undefined);
        }}
        content={
          <div style={{ height: '100%', overflow: 'auto' }}>
            <AgxColumn padding="spacing-4xl" fill>
              <AgxTypography
                id="support-migrations-error-content"
                type="body"
                size="small"
                style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
              >
                {migrationError}
              </AgxTypography>
            </AgxColumn>
          </div>
        }
      />
    </AgxColumn>
  );
};

export default Migrations;
